import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import leaf from '../../assets/images/leaf.svg';
import ExportButton from '../../components/ExportButton';
import { useNavigate } from 'react-router-dom';
import { DiagnosisPageUrl } from '../../url/poenUrl';
import SellerBatteryService from '../../api/service/seller/SellerBatteryService';
import {
  BatteryInfoData,
  BatteryMeasurements,
} from '../../api/service/BatteryInterfaces';
import generateDiagnosisTab3PDF from './DiagnosisTab3PDF';
import LoadingOverlay from '../../components/LoadingOverlay';

const Container = styled.div`
  margin-top: 10px;
  @media (max-width: 968px) {
    margin-top: 0px;
  }
`;

const TitleContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  img {
    width: 20px;
  }
`;

const Title = styled.h3`
  max-width: 800px;
  width: 100%;
  display: flex;
  line-height: 32px;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.textColor};

  img {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    margin-left: 5px;
    font-size: 12px;
    line-height: 20px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  gap: 20px;
  background-color: ${(props) => props.theme.colors.grayColor};
  border-radius: 10px 10px 0 0;

  @media (max-width: 1280px) {
    flex-direction: column;
  }
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const HeaderInfoBox = styled.div`
  flex: 1;
`;

const HeaderInfoBox2 = styled.div`
  flex: 0.6;
  @media (max-width: 1440px) {
    flex: 0.5;
  }
`;

const InfoBox = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr); /* 작은 화면에서는 두 열로 표시 */
  gap: 5px; /* 간격 조정 */
  border-radius: 20px;
  padding: 20px;
  border: 2px solid ${(props) => props.theme.colors.whiteColor};

  @media (max-width: 768px) {
    padding: 10px;
    border-radius: 10px;
    grid-template-columns: repeat(2, 1fr); /* 작은 화면에서는 두 열로 표시 */
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  label {
    text-align: right;
    font-size: 16px;
    min-width: 70px;
  }

  input {
    width: 100%;
    flex: 1;
    padding: 5px 10px;
    border: 1px solid ${(props) => props.theme.colors.grayColor};
    background-color: ${(props) => props.theme.colors.whiteColor};

    &[readonly] {
      background-color: #ebebeb;
    }
  }

  @media (max-width: 768px) {
    label {
      font-size: 10px;
      min-width: 40px;
    }

    input {
      font-size: 10px;
    }
  }
`;

const Text = styled.div`
  width: 100%;
  flex: 1;
  min-height: 28px;
  padding: 5px 10px;
  border: 1px solid ${(props) => props.theme.colors.grayColor};
  background-color: ${(props) => props.theme.colors.whiteColor};
  @media (max-width: 768px) {
    font-size: 10px;
    min-height: 20px;
  }
`;

const BodySection = styled.section`
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
  background-color: ${(props) => props.theme.colors.whiteColor};
  @media (max-width: 968px) {
    flex-direction: column;
    gap: 5px;
  }
`;

const CategoryList = styled.ul`
  min-height: 40vh;
  list-style: none;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${(props) => props.theme.colors.grayColor};
  border-left: 1px solid ${(props) => props.theme.colors.grayColor};
  @media (max-width: 1280px) {
    width: 100%;
    padding-top: 20px;
    min-height: auto;
    flex-direction: row;
    border: none;
  }
`;

const Category = styled.div`
  text-align: center;
  cursor: pointer;
  padding: 10px 50px;
  background-color: ${(props) => props.theme.colors.subColor};
  color: ${(props) => props.theme.colors.whiteColor};
  @media (max-width: 1280px) {
    display: none;
  }
`;

const CategoryItem = styled.li<{ $isActive: boolean }>`
  text-align: center;
  cursor: pointer;
  padding: 30px 50px;
  background-color: ${({ $isActive, theme }) =>
    $isActive ? '#DEEEE8' : theme.colors.whiteColor};
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.mainColor : theme.colors.textColor};
  border-bottom: 1px solid ${(props) => props.theme.colors.grayColor};

  &:nth-child(3) {
    border-left: none;
    border-right: none;
  }
  @media (max-width: 1280px) {
    border: 1px solid ${(props) => props.theme.colors.grayColor};
    flex: 1;
    font-size: 16px;
    padding: 5px 10px;
    height: 24px;
  }
  @media (max-width: 968px) {
    font-size: 12px;
  }
`;

const BodyInfoBox = styled.section`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 10px;
  flex: 1;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  max-width: 800px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};

  tr {
    border-bottom: 1px solid ${(props) => props.theme.colors.silverColor};
  }

  th,
  td {
    font-size: 16px;
    padding: 10px;
    text-align: center;
    vertical-align: middle; /* 세로 정렬 */
    white-space: nowrap; /* 줄바꿈 방지 */
  }

  th {
    background-color: ${(props) => props.theme.colors.brownColor};
    color: white;
    font-weight: 500;
  }

  th:first-child {
    border-top-left-radius: 10px;
  }

  th:last-child {
    border-top-right-radius: 10px;
  }

  @media (max-width: 768px) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;

    th {
      background-color: ${(props) => props.theme.colors.subColor};
    }

    th,
    td {
      font-size: 12px;
      padding: 10px;
      text-align: center;
      vertical-align: middle; /* 세로 정렬 */
      white-space: nowrap; /* 줄바꿈 방지 */
    }

    th {
      font-weight: 400;
    }

    th:first-child {
      border-top-left-radius: 0px;
    }

    th:last-child {
      border-top-right-radius: 0px;
    }
  }
`;

const ButtonContainerM = styled.div`
  display: none;
  gap: 10px;
  @media (max-width: 1280px) {
    display: flex;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  @media (max-width: 1280px) {
    display: none;
  }
`;

function DiagnosisTab3() {
  const categories = [
    { ko: '센서데이터', en: 'SENSOR_DATA' },
    { ko: '배터리 셀 전압', en: 'BATTERY_CELL_VOLTAGE' },
    { ko: '고장코드', en: 'FAILURE_CODE' },
  ];

  const navigate = useNavigate();
  const [isPDFLoading, setIsPDFLoading] = useState(false); // 로딩 상태 추가
  const [batteryId, setBatteryId] = useState<string | null>(null);
  const [batteryInfo, setBatteryInfo] = useState<BatteryInfoData | null>(null); // 배터리 정보 상태 추가
  const [selectedCategory, setSelectedCategory] = useState('SENSOR_DATA');

  const [tableData, setTableData] = useState<BatteryMeasurements[]>([]); // API에서 가져온 데이터를 저장
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef<HTMLDivElement | null>(null);

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setTableData([]); // 기존 데이터 초기화
    setPage(0); // 페이지 초기화
    setHasMore(true); // 더 불러올 데이터가 있음을 초기화
  };

  useEffect(() => {
    // 로컬스토리지에서 'DiagnosisbatteryId'를 가져옵니다.
    const storedBatteryId = sessionStorage.getItem('DiagnosisbatteryId');

    if (storedBatteryId) {
      setBatteryId(storedBatteryId); // 로컬스토리지에 값이 있으면 batteryId 상태에 저장
    } else {
      // 배터리 ID가 없으면 경고창을 띄우고 DiagnosisTab1로 돌아갑니다.
      alert('배터리를 선택해 주세요!');
      navigate(`${DiagnosisPageUrl}?activeTab=0`); // DiagnosisPage의 첫 번째 탭으로 이동
    }
  }, [navigate]);

  useEffect(() => {
    if (batteryId) {
      fetchBatteryDamageInfoData(batteryId);
    }
  }, [batteryId]); // batteryId가 변경될 때마다 호출

  const fetchBatteryMeasurements = async (category: string, page: number) => {
    if (!batteryId || loading) return;
    setLoading(true); // 로딩 시작

    try {
      const response = await SellerBatteryService.getBatteryMeasurements(
        batteryId,
        page,
        10, // 한 페이지에 표시할 데이터 수
        category
      );

      const newData = response.data.content;
      setTableData((prev) => [...prev, ...newData]);
      setHasMore(newData.length > 0); // 데이터가 없으면 더 이상 호출하지 않음
    } catch (error) {
      console.error('데이터 가져오기 실패:', error);
    } finally {
      setLoading(false); // 로딩 종료
    }
  };

  useEffect(() => {
    fetchBatteryMeasurements(selectedCategory, page);
  }, [selectedCategory, page, batteryId]);

  const fetchBatteryDamageInfoData = (batteryId: string) => {
    SellerBatteryService.getBatteryInfo(batteryId)
      .then((data) => {
        setBatteryInfo(data.data); // 데이터를 상태에 저장
      })
      .catch((error) => {
        console.error('배터리 기본 정보 조회 실패:', error);
      });
  };

  // PDF 다운로드 함수
  const handleDownloadPDF = async () => {
    if (batteryInfo && batteryId) {
      try {
        setIsPDFLoading(true); // 로딩 시작
        const pdfBytes = await generateDiagnosisTab3PDF(batteryInfo, batteryId); // PDF 생성
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = '진단정보_진단데이터.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('PDF 생성 중 오류 발생:', error);
      } finally {
        setIsPDFLoading(false); // 로딩 종료
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) observer.observe(observerRef.current);

    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current);
    };
  }, [hasMore, loading]);

  return (
    <Container>
      {/* 기본 정보 및 외부 손상 상태 */}
      {isPDFLoading && <LoadingOverlay />}
      <HeaderSection>
        <HeaderInfoBox>
          <TitleContainer>
            <Title>기본정보</Title>
            <ButtonContainerM>
              <ExportButton onClick={handleDownloadPDF} />
            </ButtonContainerM>
          </TitleContainer>
          <InfoBox>
            <InputContainer>
              <label>제조사</label>
              <Text>{batteryInfo?.manufacturer || ''}</Text>
            </InputContainer>
            <InputContainer>
              <label>차종</label>
              <Text>{batteryInfo?.model || ''}</Text>
            </InputContainer>
            <InputContainer>
              <label>측정일</label>
              <Text>{batteryInfo?.measurementDate || ''}</Text>
            </InputContainer>
            <InputContainer>
              <label>ROM ID</label>
              <Text>{batteryInfo?.romId || ''}</Text>
            </InputContainer>
            <InputContainer>
              <label>제품번호</label>
              <Text>{batteryInfo?.productNo || ''}</Text>
            </InputContainer>
            <InputContainer>
              <label>생산일</label>
              <Text>{batteryInfo?.productionDate || ''}</Text>
            </InputContainer>
            <InputContainer>
              <label>차량번호</label>
              <Text>{batteryInfo?.carNo || ''}</Text>
            </InputContainer>
            <InputContainer>
              <label>QR CODE</label>
              <Text>{batteryInfo?.qrcode || ''}</Text>
            </InputContainer>
          </InfoBox>
        </HeaderInfoBox>
        <HeaderInfoBox2>
          <ButtonContainer>
            <ExportButton onClick={handleDownloadPDF} />
          </ButtonContainer>
        </HeaderInfoBox2>
      </HeaderSection>
      {/* 측정 결과 및 외관 사진 */}
      <BodySection>
        <CategoryList>
          <Category>카테고리</Category>
          {categories.map((category) => (
            <CategoryItem
              key={category.en}
              $isActive={selectedCategory === category.en}
              onClick={() => handleCategoryClick(category.en)}
            >
              {category.ko}
            </CategoryItem>
          ))}
        </CategoryList>
        <BodyInfoBox>
          <TitleContainer>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              <Title>
                <img src={leaf} alt="leaf" />
                측정결과
              </Title>
            </div>
          </TitleContainer>
          <TableContainer>
            <Table>
              <thead>
                {selectedCategory === 'FAILURE_CODE' ? (
                  <tr>
                    <th>NO</th>
                    <th>상태</th>
                    <th>고장코드</th>
                    <th>고장코드명</th>
                  </tr>
                ) : (
                  <tr>
                    <th>NO</th>
                    <th>센서명</th>
                    <th>측정값</th>
                    <th>단위</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {tableData.length === 0 && (
                  <tr>
                    <td colSpan={4}> 데이터가 없습니다.</td>
                  </tr>
                )}
                {tableData.map((row, index) => (
                  <tr key={index + 1}>
                    <td>{index + 1}</td>
                    {selectedCategory === 'FAILURE_CODE' ? (
                      <>
                        <td>{row.codeStatus}</td>
                        <td>{row.codeValue}</td>
                        <td>{row.codeName}</td>
                      </>
                    ) : (
                      <>
                        <td>{row.sensorName}</td>
                        <td>{row.sensorResult}</td>
                        <td>{row.sensorUnit}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          {/* 로딩 메시지 */}
          {loading && <div style={{ textAlign: 'center' }}>로딩 중...</div>}
          {/* 관찰 대상 요소 */}
          <div ref={observerRef} style={{ height: '1px' }} />
        </BodyInfoBox>
      </BodySection>
    </Container>
  );
}

export default DiagnosisTab3;
